import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mattersApi, tasksApi, clientsApi, commentsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Plus, CheckSquare, MessageSquare, Clock, 
  Calendar, AlertTriangle, FolderKanban, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const TURTLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/r0l0976o_Transparent%20BG%20Turtle.png';

const MatterDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [matter, setMatter] = useState(null);
  const [client, setClient] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'todo',
    estimated_hours: 0
  });

  const fetchMatter = async () => {
    try {
      const matterRes = await mattersApi.getById(id);
      setMatter(matterRes.data);
      
      if (matterRes.data.client_id) {
        const clientRes = await clientsApi.getById(matterRes.data.client_id);
        setClient(clientRes.data);
      }
      
      const commentsRes = await commentsApi.getAll('matter', id);
      setComments(commentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch matter details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatter();
  }, [id]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await tasksApi.create({ ...newTask, matter_id: id, client_id: matter?.client_id });
      toast.success('Task created successfully!');
      setTaskDialogOpen(false);
      setNewTask({ title: '', description: '', priority: 'medium', status: 'todo', estimated_hours: 0 });
      fetchMatter();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      await commentsApi.create({
        entity_type: 'matter',
        entity_id: id,
        content: newComment,
        mentions: []
      });
      toast.success('Comment added');
      setNewComment('');
      const commentsRes = await commentsApi.getAll('matter', id);
      setComments(commentsRes.data);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'active': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'planning': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'todo': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  if (!matter) {
    return (
      <div className="empty-state py-16">
        <img src={TURTLE_MASCOT} alt="Turtle mascot" className="w-32 h-32 mb-4" />
        <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">Matter not found</h3>
        <Link to="/matters">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Matters
          </Button>
        </Link>
      </div>
    );
  }

  const openTasks = matter.tasks?.filter(t => t.status !== 'done').length || 0;
  const completedTasks = matter.tasks?.filter(t => t.status === 'done').length || 0;

  return (
    <div className="space-y-6" data-testid="matter-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link to="/matters">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="w-14 h-14 rounded-xl bg-[#40CFDE]/10 flex items-center justify-center">
            <FolderKanban className="w-7 h-7 text-[#40CFDE]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{matter.title}</h1>
            {client && (
              <Link to={`/clients/${client.id}`} className="text-navy/60 hover:text-[#106EFD]">
                {client.name}
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getStageColor(matter.stage)}>{matter.stage}</Badge>
          <Badge className={getPriorityColor(matter.priority)}>{matter.priority}</Badge>
          {matter.risk_level === 'high' && (
            <Badge className="bg-red-100 text-red-700">
              <AlertTriangle className="w-3 h-3 mr-1" />
              High Risk
            </Badge>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#37C563]/10 flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-[#37C563]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{completedTasks}</p>
              <p className="text-xs text-navy/60">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF801D]/10 flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-[#FF801D]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{openTasks}</p>
              <p className="text-xs text-navy/60">Open Tasks</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#106EFD]/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#106EFD]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{matter.estimated_hours || 0}h</p>
              <p className="text-xs text-navy/60">Estimated</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E171EA]/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#E171EA]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{matter.actual_hours || 0}h</p>
              <p className="text-xs text-navy/60">Actual</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tasks" className="space-y-4">
        <TabsList className="bg-white border border-navy/10">
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="comments">Comments ({comments.length})</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card className="bg-white border-navy/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Amaranth']">Tasks</CardTitle>
              <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" size="sm" data-testid="add-task-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-['Amaranth']">Add New Task</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        placeholder="Task title"
                        required
                        data-testid="task-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={newTask.description}
                        onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        placeholder="Brief description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Est. Hours</Label>
                        <Input
                          type="number"
                          value={newTask.estimated_hours}
                          onChange={(e) => setNewTask({ ...newTask, estimated_hours: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-task-btn">Create Task</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {matter.tasks?.length === 0 ? (
                <div className="empty-state py-8">
                  <img src={TURTLE_MASCOT} alt="Turtle" className="w-20 h-20 mb-3" />
                  <p className="text-navy/60">No tasks yet. Create the first one!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {matter.tasks?.map((task) => (
                    <div key={task.id} className="p-3 rounded-lg border border-navy/10 hover:border-navy/20 transition-all flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-[#1A2B5E]">{task.title}</h4>
                        {task.description && <p className="text-xs text-navy/50 mt-1">{task.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(task.status)}>{task.status?.replace('_', ' ')}</Badge>
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comments Tab */}
        <TabsContent value="comments">
          <Card className="bg-white border-navy/10">
            <CardHeader>
              <CardTitle className="font-['Amaranth']">Discussion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6">
                {comments.length === 0 ? (
                  <p className="text-center text-navy/50 py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="p-4 rounded-lg bg-navy/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-[#1A2B5E]">{comment.author_name}</span>
                        <span className="text-xs text-navy/40">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-navy/70">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>
              
              <form onSubmit={handleAddComment} className="flex gap-2">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1"
                  data-testid="comment-input"
                />
                <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="add-comment-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card className="bg-white border-navy/10">
            <CardHeader>
              <CardTitle className="font-['Amaranth']">Matter Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-navy/60">Title</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">{matter.title}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Client</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">{client?.name || 'Unknown'}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Stage</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1 capitalize">{matter.stage}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Priority</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1 capitalize">{matter.priority}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Risk Level</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1 capitalize">{matter.risk_level || 'Low'}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Due Date</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">
                    {matter.due_date ? new Date(matter.due_date).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>
              {matter.description && (
                <div>
                  <Label className="text-navy/60">Description</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">{matter.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MatterDetail;
